/**
 * ModuleWrapper - Unified module wrapper component
 *
 * This component consolidates the functionality of the old ModuleWrapper and DraggableModuleWrapper.
 * It provides edit controls (Edit, Settings, Delete) and optional drag-and-drop functionality.
 *
 * Drag functionality is automatically enabled when onDragStart callback is provided,
 * or can be explicitly controlled via the enableDrag prop.
 */
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useDrag, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { SimpleButton, Flex, Icon, theme } from '../../../../ui';
import { getModuleCapabilities } from '../../moduleConfig';
import { toRgba } from '../../../../ui/theme/colors';
import { isModuleEmpty, isModuleContentUnsupported, getModuleTitle, getModuleIconComponent } from '../../utils/moduleUtils';
import { ItemTypes } from '../../utils/dragTypes';
import DragPreview from './DragPreview';
import MinimalModuleView from './MinimalModuleView';
import EmptyStatePlaceholder from './EmptyStatePlaceholder';
import UnsupportedContentPlaceholder from './UnsupportedContentPlaceholder';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 10px;
  transition: opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.01);
    box-shadow: 0 0 20px ${toRgba(theme.colors.NEU_999, 0.12)};
  }

  &:hover .module-edit-controls,
  &:hover .drag-handle {
    opacity: 1;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;

  /* Always disable pointer events on iframes and embedded content when drag is enabled */
  ${({ $enableDrag }) =>
    $enableDrag &&
    `
    & iframe,
    & video,
    & embed,
    & object {
      pointer-events: none;
    }
  `}
`;

const DragHandle = styled.div`
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 15;
  opacity: 0;
  pointer-events: auto;
  transition: opacity 0.2s ease-in-out;
  cursor: grab;
  background: ${theme.colors.NEU_0};
  padding: 8px 6px;
  border-radius: 4px;
  box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.15)};
  border: 1px solid ${toRgba(theme.colors.NEU_999, 0.08)};
  touch-action: none;
  height: 46px;
  align-content: flex-end;

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const EditControlsContainer = styled.div`
  position: absolute;
  top: 0;
  right: 8px;
  height: 100%;
  z-index: 10;
  pointer-events: none;
`;

const EditControlsWrapper = styled.div`
  position: sticky;
  top: 12px;
  pointer-events: auto;
`;

const EditControls = styled(Flex)`
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  gap: 4px;
  background: ${theme.colors.NEU_0};
  padding: 6px;
  border-radius: 4px;
  box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.15)};
  border: 1px solid ${toRgba(theme.colors.NEU_999, 0.08)};
`;

/**
 * ModuleWrapper - Unified wrapper for modules in edit mode
 * Supports both drag-and-drop and non-draggable edit modes
 *
 * @param {Object} props
 * @param {Object} props.module - The module data
 * @param {React.ReactNode} props.children - The module component to render
 * @param {Function} props.onEditContent - Callback when edit content is clicked
 * @param {Function} props.onEditSettings - Callback when edit settings is clicked
 * @param {Function} props.onDelete - Callback when delete is clicked
 * @param {Function} props.onDragStart - Optional callback when drag starts
 * @param {Function} props.onDragEnd - Optional callback when drag ends
 * @param {boolean} props.isEditMode - Whether we're in edit mode
 * @param {boolean} props.pageInDragState - Whether ANY module on the page is being dragged
 * @param {boolean} props.isDraggedModule - Whether this module is the one currently being dragged
 * @param {boolean} props.enableDrag - Whether to enable drag-and-drop
 * @param {string} props.themeColor - Course theme color for visual styling
 */
const ModuleWrapper = ({
  module,
  children,
  onEditContent,
  onEditSettings,
  onDelete,
  onDragStart,
  onDragEnd,
  isEditMode = false,
  pageInDragState = false,
  isDraggedModule = false,
  enableDrag = !!onDragStart,
  themeColor,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const wrapperRef = useRef(null);
  const hasScrolledIntoViewRef = useRef(false);

  const moduleType = module?.type?.name;
  const capabilities = getModuleCapabilities(moduleType);
  const isEmpty = isModuleEmpty(module);
  const isUnsupported = isModuleContentUnsupported(module);
  const moduleTitle = getModuleTitle(module);
  const ModuleIcon = getModuleIconComponent(moduleType);

  // Set up react-dnd drag hook
  const [{ isDraggingDnd }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.MODULE,
      item: (monitor) => {
        const sourceOffset = monitor.getSourceClientOffset();
        if (sourceOffset) {
          setDragPosition({ x: sourceOffset.x, y: sourceOffset.y });
        }
        return {
          moduleId: module.id,
          moduleType: moduleType,
          rowId: module.pageRowId,
        };
      },
      collect: (monitor) => ({
        isDraggingDnd: monitor.isDragging(),
      }),
      canDrag: isEditMode && enableDrag,
    }),
    [isEditMode, enableDrag, module.id, moduleType, module.pageRowId],
  );

  // Hide the default HTML5 drag preview
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Track drag state from monitor
  useEffect(() => {
    if (isDraggingDnd && !isDragging) {
      setIsDragging(true);
      if (onDragStart) {
        onDragStart(module);
      }
    } else if (!isDraggingDnd && isDragging) {
      setIsDragging(false);
      if (onDragEnd) {
        onDragEnd(module);
      }
    }
  }, [isDraggingDnd, isDragging, module, onDragStart, onDragEnd]);

  // Use useDragLayer to track cursor position during drag
  const { currentOffset } = useDragLayer((monitor) => ({
    currentOffset: monitor.getClientOffset(),
  }));

  // Update drag position when cursor moves
  useEffect(() => {
    if (isDraggedModule && currentOffset) {
      setDragPosition({ x: currentOffset.x, y: currentOffset.y });
    }
  }, [isDraggedModule, currentOffset]);

  const handleEditContent = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onEditContent) {
      onEditContent(module);
    }
  };

  const handleEditSettings = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onEditSettings) {
      onEditSettings(module);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDelete) {
      onDelete(module);
    }
  };

  // Consolidated dragging state for UI
  const isDraggingForUI = isDragging || isDraggedModule;
  const showMinimalView = pageInDragState;

  // MinimalModuleView colors
  const minimalBgColor = isDraggingForUI ? toRgba(themeColor, 0.03) : theme.colors.NEU_0;
  const minimalBorderColor = isDraggingForUI ? toRgba(themeColor, 0.2) : theme.colors.NEU_100;
  const minimalShadow = isDraggingForUI ? `0 2px 8px ${toRgba(themeColor, 0.08)}` : 'none';
  const minimalHoverBorderColor = toRgba(themeColor, 0.3);
  const minimalHoverShadow = `0 2px 8px ${toRgba(themeColor, 0.12)}`;
  const minimalOpacity = isDraggingForUI ? 0.4 : 1;
  const minimalHoverOpacity = isDraggingForUI ? 0.5 : 1;

  // Auto-scroll dragged module into view
  useEffect(() => {
    if (pageInDragState && isDraggingForUI && !hasScrolledIntoViewRef.current) {
      hasScrolledIntoViewRef.current = true;
      if (wrapperRef.current && typeof wrapperRef.current.scrollIntoView === 'function') {
        try {
          wrapperRef.current.scrollIntoView({ block: 'center', behavior: 'auto' });
        } catch {
          // Ignore scroll errors
        }
      }
    } else if (!pageInDragState && hasScrolledIntoViewRef.current) {
      hasScrolledIntoViewRef.current = false;
    }
  }, [pageInDragState, isDraggingForUI]);

  // DragPreview colors
  const dragPreviewBgColor = toRgba(themeColor, 0.15);
  const dragPreviewShadowLight = `0 8px 24px ${toRgba(themeColor, 0.4)}`;
  const dragPreviewShadowStrong = `0 8px 32px ${toRgba(themeColor, 0.6)}`;

  if (!isEditMode) {
    return children;
  }

  return (
    <>
      <Wrapper ref={wrapperRef} $isDragging={isDraggingForUI} $showMinimalView={pageInDragState} $themeColor={themeColor}>
        {enableDrag && (
          <DragHandle ref={drag} className='drag-handle'>
            <Icon.DragIndicator size='small' color={theme.colors.NEU_500} />
          </DragHandle>
        )}

        {showMinimalView ? (
          <MinimalModuleView
            moduleType={moduleType}
            moduleTitle={moduleTitle}
            IconComponent={ModuleIcon}
            themeColor={themeColor}
            bgColor={minimalBgColor}
            borderColor={minimalBorderColor}
            shadow={minimalShadow}
            hoverBorderColor={minimalHoverBorderColor}
            hoverShadow={minimalHoverShadow}
            opacity={minimalOpacity}
            hoverOpacity={minimalHoverOpacity}
          />
        ) : (
          <>
            <EditControlsContainer>
              <EditControlsWrapper>
                <EditControls className='module-edit-controls' direction='column' align='flex-start'>
                  {capabilities.hasContent && (
                    <SimpleButton
                      startIcon='Edit'
                      onClick={handleEditContent}
                      variant='ghost'
                      size='small'
                      fullWidth
                      justifyContent='flex-start'
                      bgColor={theme.colors.PRIMARY_LIGHT}
                      square
                      aria-label={t('editCourse.editContentAriaLabel')}
                    >
                      {t('editCourse.editContentAriaLabel')}
                    </SimpleButton>
                  )}
                  {capabilities.hasSettings && (
                    <SimpleButton
                      startIcon='Settings'
                      onClick={handleEditSettings}
                      variant='ghost'
                      size='small'
                      fullWidth
                      justifyContent='flex-start'
                      square
                      aria-label={t('editCourse.editSettingsAriaLabel')}
                    >
                      {t('editCourse.editSettingsAriaLabel')}
                    </SimpleButton>
                  )}
                  <SimpleButton
                    startIcon='Delete'
                    onClick={handleDelete}
                    bgColor={theme.colors.ACTION}
                    variant='ghost'
                    size='small'
                    fullWidth
                    justifyContent='flex-start'
                    square
                    aria-label={t('editCourse.deleteModuleAriaLabel')}
                  >
                    {t('editCourse.deleteModuleAriaLabel')}
                  </SimpleButton>
                </EditControls>
              </EditControlsWrapper>
            </EditControlsContainer>

            <ContentWrapper $enableDrag={enableDrag}>
              {isEmpty ? (
                <EmptyStatePlaceholder moduleType={moduleType} IconComponent={ModuleIcon} themeColor={themeColor} />
              ) : isUnsupported ? (
                <UnsupportedContentPlaceholder moduleType={moduleType} IconComponent={ModuleIcon} themeColor={themeColor} />
              ) : (
                children
              )}
            </ContentWrapper>
          </>
        )}
      </Wrapper>

      {enableDrag && isDraggedModule && pageInDragState && (
        <DragPreview
          x={dragPosition.x}
          y={dragPosition.y}
          moduleType={moduleType}
          IconComponent={ModuleIcon}
          themeColor={themeColor}
          bgColor={dragPreviewBgColor}
          shadowLight={dragPreviewShadowLight}
          shadowStrong={dragPreviewShadowStrong}
        />
      )}
    </>
  );
};

export default ModuleWrapper;
